import { ReactNode } from "react";

/**
 * The uppercase field-label typography used by the automation action value
 * editors. Copied verbatim from TaskForm.tsx's FIELD_LABEL_CLASS so the
 * builder's "Create a Task" block matches the standalone task form it authors.
 *
 * Exported for the one case that labels a GROUP of controls rather than a
 * single one, where a <label for> would have to pick a winner among them.
 */
export const ACTION_FIELD_LABEL_CLASS =
  "text-xs font-medium uppercase tracking-wide text-muted-foreground";

export interface ActionFieldLabelProps {
  /**
   * The id of the control this label names. Required on purpose: a <label>
   * with no htmlFor renders correctly and gives assistive technology nothing,
   * and an optional prop would make that the easy default.
   */
  htmlFor: string;
  /**
   * Renders the required marker. Only for fields whose value the save path
   * actually rejects when empty — asterisking everything destroys the signal.
   */
  required?: boolean;
  children: ReactNode;
}

/**
 * A field label for the automation action value editors, with an optional
 * required marker.
 * @param {ActionFieldLabelProps} props - The label's target, requiredness, and text.
 * @returns {JSX.Element} The rendered label.
 */
export const ActionFieldLabel = ({
  htmlFor,
  required,
  children,
}: ActionFieldLabelProps) => (
  <label className={ACTION_FIELD_LABEL_CLASS} htmlFor={htmlFor}>
    {children}
    {required && (
      <>
        {" "}
        {/*
         * text-destructive, NOT text-red-500: tailwind.config.js sets
         * `red` to a flat hex string, which replaces Tailwind's default red
         * scale, so every text-red-<N> class is silently dropped at build time.
         */}
        <span className={"text-destructive"}>*</span>
      </>
    )}
  </label>
);
